import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React from 'react';

import { SelectInput } from '../../../../components/Forms/selectInput';
import { UnitType } from '../../../../types';

export interface AddMembershipUnitPayload {
  source: 'existing' | 'new';
  unitLabel: string;
  vinNumber: string;
  existingUnitId?: string;
  linkedDriverId?: string;
  linkedDriverName?: string;
  unit?: {
    primaryDriverId: string;
    unitNumber: string;
    unitType: string;
    vehicleMake?: string;
    vehicleModel?: string;
    vehicleYear?: string;
    vinNumber: string;
    grossVehicleWeight?: string;
  };
}

export interface ExistingUnitOption {
  unitId: string;
  unitLabel: string;
  vinNumber: string;
}

export interface ExistingDriverOption {
  driverId: string;
  driverName: string;
}

const buildExistingUnitValue = (unit: ExistingUnitOption) => unit.unitId;

const buildExistingUnitDisplay = (unit: ExistingUnitOption) =>
  `${unit.unitLabel || 'Unit'} · ${unit.vinNumber || 'No VIN'}`;

interface AddMembershipUnitModalProps {
  existingDrivers: ExistingDriverOption[];
  existingUnits: ExistingUnitOption[];
  showDriverView?: boolean;
  isLoadingExistingDrivers?: boolean;
  isLoadingExistingUnits?: boolean;
  onClose: () => void;
  onSubmit: (payload: AddMembershipUnitPayload) => Promise<boolean>;
  open: boolean;
}

type UnitWizardStep =
  | 'choice'
  | 'existing'
  | 'existingDone'
  | 'newUnitInfo'
  | 'linkDriver'
  | 'review'
  | 'done';

type ReviewSource = 'existing' | 'new';

const UNIT_TYPE_OPTIONS = [
  UnitType.Truck,
  UnitType.Tractor,
  UnitType.Bus,
  UnitType.Tanker,
  UnitType.Van,
  UnitType.Limousine,
  UnitType.PickUpTruck,
  UnitType.Other,
];

const UNIT_NUMBER_REGEX = /^\w+$/;
const VIN_REGEX = /^[0-9a-zA-Z]*$/;
const GVW_REGEX = /^[0-9]*$/;
const YEAR_REGEX = /^\d{4}$/;
const VEHICLE_MAKE_REGEX = /^[0-9a-zA-Z -]*$/;

const AddMembershipUnitModal: React.FC<AddMembershipUnitModalProps> = ({
  existingDrivers,
  existingUnits,
  showDriverView = false,
  isLoadingExistingDrivers = false,
  isLoadingExistingUnits = false,
  onClose,
  onSubmit,
  open,
}) => {
  const [step, setStep] = React.useState<UnitWizardStep>('choice');
  const [existingUnit, setExistingUnit] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [unitNumber, setUnitNumber] = React.useState('');
  const [vinNumber, setVinNumber] = React.useState('');
  const [unitGvw, setUnitGvw] = React.useState('');
  const [unitYear, setUnitYear] = React.useState('');
  const [unitMake, setUnitMake] = React.useState('');
  const [unitModel, setUnitModel] = React.useState('');
  const [unitType, setUnitType] = React.useState('');
  const [linkedDriverId, setLinkedDriverId] = React.useState('');
  const [newUnitErrors, setNewUnitErrors] = React.useState<
    Record<string, string>
  >({});
  const [showNewUnitErrors, setShowNewUnitErrors] = React.useState(false);
  const [showDriverError, setShowDriverError] = React.useState(false);
  const [reviewSource, setReviewSource] = React.useState<ReviewSource>('new');

  const resetAndClose = React.useCallback(() => {
    setStep('choice');
    setExistingUnit('');
    setIsSubmitting(false);
    setUnitNumber('');
    setVinNumber('');
    setUnitGvw('');
    setUnitYear('');
    setUnitMake('');
    setUnitModel('');
    setUnitType('');
    setLinkedDriverId('');
    setNewUnitErrors({});
    setShowNewUnitErrors(false);
    setShowDriverError(false);
    setReviewSource('new');
    onClose();
  }, [onClose]);

  React.useEffect(() => {
    if (!open) {
      setStep('choice');
      setExistingUnit('');
      setIsSubmitting(false);
      setUnitNumber('');
      setVinNumber('');
      setUnitGvw('');
      setUnitYear('');
      setUnitMake('');
      setUnitModel('');
      setUnitType('');
      setLinkedDriverId('');
      setNewUnitErrors({});
      setShowNewUnitErrors(false);
      setShowDriverError(false);
      setReviewSource('new');
    }
  }, [showDriverView, open]);

  const validateNewUnit = React.useCallback(() => {
    const errors: Record<string, string> = {};

    if (!unitNumber.trim()) {
      errors.unitNumber = 'Required';
    } else if (!UNIT_NUMBER_REGEX.test(unitNumber.trim())) {
      errors.unitNumber = 'Enter valid unit number';
    }

    const vin = vinNumber.trim();
    if (!vin) {
      errors.vinNumber = 'Required';
    } else if (vin.length < 8) {
      errors.vinNumber = 'Enter minimum 8 character';
    } else if (vin.length > 17) {
      errors.vinNumber = 'Enter maximum 17 character';
    } else if (!VIN_REGEX.test(vin)) {
      errors.vinNumber = 'Enter valid Vin';
    }

    if (!unitGvw.trim()) {
      errors.unitGvw = 'Required';
    } else if (!GVW_REGEX.test(unitGvw.trim())) {
      errors.unitGvw = 'Enter valid Gross vehicle weight';
    }

    if (!unitYear.trim()) {
      errors.unitYear = 'Required';
    } else if (!YEAR_REGEX.test(unitYear.trim())) {
      errors.unitYear = 'Enter a valid 4-digit year';
    }

    if (!unitMake.trim()) {
      errors.unitMake = 'Required';
    } else if (!VEHICLE_MAKE_REGEX.test(unitMake.trim())) {
      errors.unitMake = 'Enter valid vehicle make';
    }

    if (!unitType.trim()) {
      errors.unitType = 'Required';
    }

    setNewUnitErrors(errors);
    return errors;
  }, [unitGvw, unitMake, unitNumber, unitType, unitYear, vinNumber]);

  React.useEffect(() => {
    if (showNewUnitErrors) {
      validateNewUnit();
    }
  }, [
    showNewUnitErrors,
    unitNumber,
    vinNumber,
    unitGvw,
    unitYear,
    unitMake,
    unitModel,
    unitType,
    validateNewUnit,
  ]);

  const unitLabel = React.useMemo(() => {
    const description = [
      unitNumber.trim(),
      unitYear.trim(),
      unitMake.trim(),
      unitModel.trim(),
    ]
      .filter(Boolean)
      .join(' ');

    return description || 'New Unit';
  }, [unitMake, unitModel, unitNumber, unitYear]);

  const selectedExistingUnit = React.useMemo(
    () =>
      existingUnits.find(
        (unit) => buildExistingUnitValue(unit) === existingUnit,
      ),
    [existingUnit, existingUnits],
  );

  const existingUnitSelectOptions = React.useMemo(
    () =>
      existingUnits.map((unit) => ({
        label: buildExistingUnitDisplay(unit),
        value: buildExistingUnitValue(unit),
      })),
    [existingUnits],
  );

  const selectedDriver = React.useMemo(
    () => existingDrivers.find((driver) => driver.driverId === linkedDriverId),
    [existingDrivers, linkedDriverId],
  );

  const driverSelectOptions = React.useMemo(
    () =>
      existingDrivers.map((driver) => ({
        label: driver.driverName,
        value: driver.driverId,
      })),
    [existingDrivers],
  );

  const submitExistingUnit = async () => {
    if (!existingUnit.trim()) {
      return;
    }

    if (!showDriverView && !linkedDriverId.trim()) {
      setShowDriverError(true);
      return;
    }

    setIsSubmitting(true);
    const created = await onSubmit({
      existingUnitId: existingUnit,
      linkedDriverId: linkedDriverId.trim() || undefined,
      linkedDriverName: (selectedDriver?.driverName || '').trim(),
      source: 'existing',
      unitLabel: (selectedExistingUnit?.unitLabel || '').trim(),
      vinNumber: (selectedExistingUnit?.vinNumber || '').trim(),
    });
    setIsSubmitting(false);

    if (created) {
      setStep('existingDone');
    }
  };

  const submitNewUnit = async () => {
    const errors = validateNewUnit();
    setShowNewUnitErrors(true);
    if (Object.keys(errors).length > 0) {
      return;
    }

    if (!showDriverView && !linkedDriverId.trim()) {
      setShowDriverError(true);
      return;
    }

    setIsSubmitting(true);
    const created = await onSubmit({
      linkedDriverId: linkedDriverId.trim() || undefined,
      linkedDriverName: (selectedDriver?.driverName || '').trim(),
      source: 'new',
      unit: {
        grossVehicleWeight: unitGvw.trim() || undefined,
        primaryDriverId: linkedDriverId.trim() || '',
        unitNumber: unitNumber.trim(),
        unitType: unitType.trim(),
        vehicleMake: unitMake.trim() || undefined,
        vehicleModel: unitModel.trim() || undefined,
        vehicleYear: unitYear.trim() || undefined,
        vinNumber: vinNumber.trim(),
      },
      unitLabel,
      vinNumber: vinNumber.trim(),
    });
    setIsSubmitting(false);

    if (created) {
      setStep('done');
    }
  };

  const headerCopy = (() => {
    if (step === 'choice') return { eyebrow: '', title: 'Add a Unit' };
    if (step === 'existing')
      return { eyebrow: 'Step 1 of 3', title: 'Select a Unit' };
    if (step === 'existingDone')
      return { eyebrow: 'Request Submitted', title: "You're All Set" };
    if (step === 'newUnitInfo')
      return showDriverView
        ? { eyebrow: 'Step 1 of 2', title: 'Unit Details' }
        : { eyebrow: 'Step 2 of 4', title: 'Unit Details' };
    if (step === 'linkDriver') {
      return reviewSource === 'existing'
        ? { eyebrow: 'Step 2 of 3', title: 'Link to a Driver' }
        : { eyebrow: 'Step 3 of 4', title: 'Link to a Driver' };
    }
    if (step === 'review') {
      return reviewSource === 'existing'
        ? { eyebrow: 'Step 3 of 3', title: 'Review & Confirm' }
        : showDriverView
        ? { eyebrow: 'Step 2 of 2', title: 'Review & Confirm' }
        : { eyebrow: 'Step 4 of 4', title: 'Review & Confirm' };
    }
    return { eyebrow: 'Request Submitted', title: "You're All Set" };
  })();

  const isNewUnitStepOneDisabled =
    !unitNumber.trim() ||
    !vinNumber.trim() ||
    !unitGvw.trim() ||
    !unitYear.trim() ||
    !unitMake.trim() ||
    !unitType.trim();
  const hasNewUnitValidationErrors = Object.keys(newUnitErrors).length > 0;
  const linkedDriverError =
    showDriverError && !linkedDriverId.trim() ? 'Required' : '';

  return (
    <Dialog fullWidth maxWidth="sm" onClose={resetAndClose} open={open}>
      <DialogTitle sx={{ padding: '16px 22px 8px 22px' }}>
        <Typography
          sx={{
            color: '#C9A200',
            fontFamily: 'FiraSans-SemiBold',
            fontSize: '11px',
            letterSpacing: '.4px',
            minHeight: '14px',
            textTransform: 'uppercase',
          }}>
          {headerCopy.eyebrow}
        </Typography>
        <Typography
          sx={{
            color: '#1A1A1A',
            fontFamily: 'FiraSans-SemiBold',
            fontSize: '20px',
          }}>
          {headerCopy.title}
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ padding: '18px 22px' }}>
        {step === 'choice' ? (
          <Box sx={{ display: 'grid', gap: '12px' }}>
            <Typography
              sx={{
                color: '#2B2B2B',
                fontFamily: 'FiraSans-SemiBold',
                fontSize: '14px',
              }}>
              How would you like to add a unit?
            </Typography>

            <Box sx={{ display: 'flex', gap: '12px' }}>
              <Button
                onClick={() => {
                  setLinkedDriverId('');
                  setStep('existing');
                }}
                sx={choiceButtonSx}
                variant="outlined">
                <Typography sx={choiceTitleSx}>From my DOT Roster</Typography>
                <Typography sx={choiceSubtitleSx}>
                  Pick an unit already on your DOT service.
                </Typography>
              </Button>

              <Button
                onClick={() => setStep('newUnitInfo')}
                sx={choiceButtonSx}
                variant="outlined">
                <Typography sx={choiceTitleSx}>Create New Unit</Typography>
                <Typography sx={choiceSubtitleSx}>
                  Enter a brand-new unit&apos;s details
                </Typography>
              </Button>
            </Box>
          </Box>
        ) : null}

        {step === 'existing' ? (
          <Box sx={{ display: 'grid', gap: '14px' }}>
            <Typography sx={fieldLabelSx}>Select a Unit *</Typography>
            <SelectInput
              name="existingUnit"
              value={existingUnit}
              onChangeText={(value) => setExistingUnit(value)}
              optionsList={existingUnitSelectOptions}
              size="small"
              placeHolder="Select a unit..."
              defaultOptionLabel="Select a unit..."
              isEditable={false}
            />

            {isLoadingExistingUnits ? (
              <Typography sx={loadingHelperSx}>
                Loading available units...
              </Typography>
            ) : null}

            {!isLoadingExistingUnits && !existingUnits.length ? (
              <Typography sx={loadingHelperSx}>
                No eligible units found.
              </Typography>
            ) : null}
          </Box>
        ) : null}

        {step === 'newUnitInfo' ? (
          <Box sx={{ display: 'grid', gap: '14px' }}>
            <Box>
              <Typography sx={fieldLabelSx}>Unit Number *</Typography>
              <TextField
                onChange={(event) => setUnitNumber(event.target.value)}
                placeholder="e.g. PU-102"
                size="small"
                sx={inputSx}
                value={unitNumber}
                error={showNewUnitErrors && !!newUnitErrors.unitNumber}
                helperText={showNewUnitErrors ? newUnitErrors.unitNumber : ''}
                fullWidth
              />
            </Box>

            <Box>
              <Typography sx={fieldLabelSx}>VIN *</Typography>
              <TextField
                onChange={(event) => setVinNumber(event.target.value)}
                placeholder="17-character VIN"
                size="small"
                sx={inputSx}
                value={vinNumber}
                error={showNewUnitErrors && !!newUnitErrors.vinNumber}
                helperText={showNewUnitErrors ? newUnitErrors.vinNumber : ''}
                fullWidth
              />
            </Box>

            <Box
              sx={{
                display: 'grid',
                gap: '12px',
                gridTemplateColumns: { sm: '1fr 1fr', xs: '1fr' },
              }}>
              <Box>
                <Typography sx={fieldLabelSx}>
                  Gross Vehicle Weight (GVW) *
                </Typography>
                <TextField
                  onChange={(event) => setUnitGvw(event.target.value)}
                  placeholder="e.g. 33,000 lbs"
                  size="small"
                  sx={inputSx}
                  value={unitGvw}
                  error={showNewUnitErrors && !!newUnitErrors.unitGvw}
                  helperText={showNewUnitErrors ? newUnitErrors.unitGvw : ''}
                  fullWidth
                />
              </Box>

              <Box>
                <Typography sx={fieldLabelSx}>Year *</Typography>
                <TextField
                  inputProps={{ maxLength: 4 }}
                  onChange={(event) => setUnitYear(event.target.value)}
                  placeholder="e.g. 2022"
                  size="small"
                  sx={inputSx}
                  value={unitYear}
                  error={showNewUnitErrors && !!newUnitErrors.unitYear}
                  helperText={showNewUnitErrors ? newUnitErrors.unitYear : ''}
                  fullWidth
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gap: '12px',
                gridTemplateColumns: { sm: '1fr 1fr', xs: '1fr' },
              }}>
              <Box>
                <Typography sx={fieldLabelSx}>Make *</Typography>
                <TextField
                  onChange={(event) => setUnitMake(event.target.value)}
                  placeholder="e.g. Freightliner"
                  size="small"
                  sx={inputSx}
                  value={unitMake}
                  error={showNewUnitErrors && !!newUnitErrors.unitMake}
                  helperText={showNewUnitErrors ? newUnitErrors.unitMake : ''}
                  fullWidth
                />
              </Box>

              <Box>
                <Typography sx={fieldLabelSx}>Model</Typography>
                <TextField
                  onChange={(event) => setUnitModel(event.target.value)}
                  placeholder="e.g. Cascadia"
                  size="small"
                  sx={inputSx}
                  value={unitModel}
                  error={showNewUnitErrors && !!newUnitErrors.unitModel}
                  helperText={showNewUnitErrors ? newUnitErrors.unitModel : ''}
                  fullWidth
                />
              </Box>
            </Box>

            <Box>
              <Typography sx={fieldLabelSx}>Unit Type *</Typography>
              <SelectInput
                name="unitType"
                value={unitType}
                onChangeText={(value) => {
                  setUnitType(value);
                }}
                optionsList={UNIT_TYPE_OPTIONS.map((typeOption) => ({
                  label: typeOption,
                  value: typeOption,
                }))}
                size="small"
                placeHolder="Select a type..."
                defaultOptionLabel="Select a type..."
                sortByOptionDisable={true}
                errors={showNewUnitErrors ? newUnitErrors.unitType || '' : ''}
                isEditable={false}
              />
            </Box>
          </Box>
        ) : null}

        {step === 'linkDriver' ? (
          <Box sx={{ display: 'grid', gap: '14px' }}>
            <Typography
              sx={{
                color: '#2B2B2B',
                fontFamily: 'FiraSans-SemiBold',
                fontSize: '14px',
              }}>
              Select the driver you want to link with this unit. *
            </Typography>

            <SelectInput
              name="linkedDriver"
              value={linkedDriverId}
              onChangeText={(value) => {
                setLinkedDriverId(value);
                setShowDriverError(false);
              }}
              optionsList={driverSelectOptions}
              size="small"
              placeHolder="Select a driver"
              defaultOptionLabel="Select a driver"
              errors={linkedDriverError}
              isEditable={false}
            />

            {isLoadingExistingDrivers ? (
              <Typography sx={loadingHelperSx}>
                Loading available drivers...
              </Typography>
            ) : null}

            {!isLoadingExistingDrivers && !existingDrivers.length ? (
              <Typography sx={loadingHelperSx}>
                No drivers available from DOT roster.
              </Typography>
            ) : null}
          </Box>
        ) : null}

        {step === 'review' ? (
          <Box
            sx={{
              backgroundColor: '#FBEED3',
              border: '1px solid #E8D39C',
              borderRadius: '10px',
              color: '#5C4004',
              fontFamily: 'FiraSans-Regular',
              fontSize: '13px',
              lineHeight: 1.6,
              padding: '14px 16px',
            }}>
            {reviewSource === 'existing' ? (
              <>
                By submitting this request, you acknowledge that{' '}
                <b>
                  {`${
                    selectedExistingUnit?.unitLabel || 'Selected Unit'
                  } (VIN ${selectedExistingUnit?.vinNumber || 'pending'})`}
                </b>
                ,
                {showDriverView ? (
                  ''
                ) : (
                  <>
                    linked driver:{' '}
                    <b>{selectedDriver?.driverName || 'No linked driver'}</b>,{' '}
                  </>
                )}{' '}
                will <b>not be immediately enrolled.</b> Our team will review
                your request and confirm eligibility and collect payment, if
                necessary.
                <br />
              </>
            ) : (
              <>
                By submitting this request, you acknowledge that{' '}
                <b>{`${unitLabel} (VIN ${vinNumber || 'pending'})`}</b>
                {showDriverView ? null : (
                  <>
                    , linked driver:{' '}
                    <b>{selectedDriver?.driverName || 'No linked driver'}</b>
                  </>
                )}
                , will <b>not be immediately enrolled.</b> Our team will review
                your request and confirm eligibility and collect payment, if
                necessary.
                <br />
              </>
            )}
          </Box>
        ) : null}

        {step === 'existingDone' || step === 'done' ? (
          <Box sx={{ padding: '12px 0 6px', textAlign: 'center' }}>
            <Typography
              sx={{
                color: '#2F8F4E',
                fontFamily: 'FiraSans-SemiBold',
                fontSize: '34px',
                lineHeight: 1,
                marginBottom: '8px',
              }}>
              ✓
            </Typography>
            <Typography
              sx={{
                color: '#1A1A1A',
                fontFamily: 'FiraSans-SemiBold',
                fontSize: '16px',
                marginBottom: '6px',
              }}>
              Great! Your request has been submitted.
            </Typography>
            <Typography
              sx={{
                color: '#66666F',
                fontFamily: 'FiraSans-Regular',
                fontSize: '13px',
                lineHeight: 1.55,
              }}>
              Our team will contact you within one business day.
            </Typography>
            <Typography
              sx={{
                color: '#66666F',
                fontFamily: 'FiraSans-Regular',
                fontSize: '13px',
                lineHeight: 1.55,
              }}>
              Thank you for being a valued Motor Club member!
            </Typography>
          </Box>
        ) : null}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ backgroundColor: '#F7F7F8', padding: '14px 22px' }}>
        {step === 'choice' ? (
          <Button onClick={resetAndClose} sx={cancelButtonSx}>
            Cancel
          </Button>
        ) : null}

        {step === 'existing' ? (
          <>
            <Button onClick={() => setStep('choice')} sx={backButtonSx}>
              {'<-'} Back
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={resetAndClose} sx={cancelButtonSx}>
              Cancel
            </Button>
            <Button
              disabled={
                !existingUnit.trim() || !existingUnits.length || isSubmitting
              }
              onClick={() => {
                if (!existingUnit.trim()) {
                  return;
                }

                setReviewSource('existing');
                setStep(showDriverView ? 'review' : 'linkDriver');
              }}
              sx={primaryButtonSx}
              variant="contained">
              Next
            </Button>
          </>
        ) : null}

        {step === 'newUnitInfo' ? (
          <>
            {showDriverView ? null : (
              <Button onClick={() => setStep('choice')} sx={backButtonSx}>
                {'<-'} Back
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button onClick={resetAndClose} sx={cancelButtonSx}>
              Cancel
            </Button>
            <Button
              disabled={isNewUnitStepOneDisabled}
              onClick={() => {
                setShowNewUnitErrors(true);
                const errors = validateNewUnit();
                if (Object.keys(errors).length === 0) {
                  setReviewSource('new');
                  setStep(showDriverView ? 'review' : 'linkDriver');
                }
              }}
              sx={primaryButtonSx}
              variant="contained">
              Next
            </Button>
          </>
        ) : null}

        {step === 'linkDriver' ? (
          <>
            <Button
              onClick={() =>
                setStep(
                  reviewSource === 'existing' ? 'existing' : 'newUnitInfo',
                )
              }
              sx={backButtonSx}>
              {'<-'} Back
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={resetAndClose} sx={cancelButtonSx}>
              Cancel
            </Button>
            <Button
              disabled={!linkedDriverId.trim()}
              onClick={() => {
                if (!linkedDriverId.trim()) {
                  setShowDriverError(true);
                  return;
                }

                setShowDriverError(false);
                setStep('review');
              }}
              sx={primaryButtonSx}
              variant="contained">
              Next
            </Button>
          </>
        ) : null}

        {step === 'review' ? (
          <>
            <Button
              onClick={() =>
                setStep(
                  showDriverView
                    ? reviewSource === 'new'
                      ? 'newUnitInfo'
                      : 'existing'
                    : 'linkDriver',
                )
              }
              sx={backButtonSx}>
              {'<-'} Back
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={resetAndClose} sx={cancelButtonSx}>
              Cancel Request
            </Button>
            <Button
              disabled={
                reviewSource === 'existing'
                  ? !existingUnit.trim() ||
                    (!showDriverView && !linkedDriverId.trim()) ||
                    isSubmitting
                  : !vinNumber.trim() ||
                    isSubmitting ||
                    hasNewUnitValidationErrors
              }
              onClick={
                reviewSource === 'existing' ? submitExistingUnit : submitNewUnit
              }
              sx={primaryButtonSx}
              variant="contained">
              {isSubmitting ? (
                <CircularProgress size={18} sx={{ color: '#241A2E' }} />
              ) : (
                'Agree'
              )}
            </Button>
          </>
        ) : null}

        {step === 'existingDone' || step === 'done' ? (
          <Button
            onClick={resetAndClose}
            sx={{ ...primaryButtonSx, width: '100%' }}
            variant="contained">
            Back to Dashboard
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

const choiceButtonSx = {
  '&:hover': {
    backgroundColor: '#F7F7F8',
    borderColor: '#DEC330',
  },
  alignItems: 'flex-start',
  borderColor: '#E2E2E6',
  borderRadius: '10px',
  color: '#2B2B2B',
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  gap: '4px',
  justifyContent: 'flex-start',
  minHeight: '84px',
  padding: '14px 16px',
  textTransform: 'none',
};

const choiceTitleSx = {
  color: '#1A1A1A',
  fontFamily: 'FiraSans-SemiBold',
  fontSize: '14px',
};

const choiceSubtitleSx = {
  color: '#66666F',
  fontFamily: 'FiraSans-Regular',
  fontSize: '12px',
  textAlign: 'left',
};

const fieldLabelSx = {
  color: '#1A1A1A',
  fontFamily: 'FiraSans-SemiBold',
  fontSize: '12px',
  marginBottom: '6px',
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
  },
  '& .MuiSelect-select': {
    color: '#2B2B2B',
    fontFamily: 'FiraSans-Regular',
    fontSize: '13px',
  },
};

const loadingHelperSx = {
  color: '#66666F',
  fontFamily: 'FiraSans-Regular',
  fontSize: '11.5px',
};

const cancelButtonSx = {
  color: '#66666F',
  fontFamily: 'FiraSans-SemiBold',
  textTransform: 'none',
};

const backButtonSx = {
  color: '#66666F',
  fontFamily: 'FiraSans-SemiBold',
  fontSize: '12px',
  minWidth: 'fit-content',
  textTransform: 'none',
};

const primaryButtonSx = {
  '&:hover': {
    backgroundColor: '#CBB12B',
  },
  backgroundColor: '#DEC330',
  borderRadius: '8px',
  color: '#241A2E',
  fontFamily: 'FiraSans-SemiBold',
  minWidth: '110px',
  textTransform: 'none',
};

export default AddMembershipUnitModal;
